import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ProviderRequestTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantLegalName: string | null;
  tenantRut: string | null;
}

/**
 * Pre-written request template the user can copy and send to their current
 * billing provider to obtain the digital certificate, passphrase, CFE
 * export, and CAE records. Non-editable on purpose — keep wording
 * consistent across users. If a user needs to soften the tone, they can
 * still paste and edit in their email client.
 */
export function ProviderRequestTemplateDialog({
  open,
  onOpenChange,
  tenantLegalName,
  tenantRut,
}: ProviderRequestTemplateDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const company =
    tenantLegalName?.trim() ||
    t(
      "onboarding.providerRequestTemplate.fallbackCompany",
      "[Nombre de tu empresa]",
    );
  const rut =
    tenantRut?.trim() ||
    t("onboarding.providerRequestTemplate.fallbackRut", "[RUT]");

  const body = t("onboarding.providerRequestTemplate.body", {
    defaultValue:
      "Hola,\n\nEstamos migrando nuestra facturación electrónica a un nuevo proveedor y necesitamos los siguientes archivos para completar la transición de {{company}} (RUT {{rut}}):\n\n1. El certificado digital (.pfx o .p12) emitido para nuestra empresa.\n2. La contraseña del certificado.\n3. Una exportación de todos los comprobantes fiscales electrónicos (CFE) generados con nuestra cuenta.\n4. Los CAE (Constancia de Autorización de Emisión) correspondientes.\n\nQuedamos atentos para cualquier coordinación.\n\nMuchas gracias.",
    company,
    rut,
  });

  const subject = t("onboarding.providerRequestTemplate.subject", {
    defaultValue: "Solicitud de certificado digital y CFE — {{company}}",
    company,
  });

  const fullEmail = `${t("onboarding.providerRequestTemplate.subjectLabel", "Asunto")}: ${subject}\n\n${body}`;

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(fullEmail);
      setCopied(true);
      toast.success(
        t(
          "onboarding.providerRequestTemplate.copiedToast",
          "Plantilla copiada",
        ),
      );
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(
        t(
          "onboarding.providerRequestTemplate.copyErrorToast",
          "No se pudo copiar — seleccioná y copiá manualmente",
        ),
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {t(
              "onboarding.providerRequestTemplate.title",
              "Plantilla para tu proveedor actual",
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              "onboarding.providerRequestTemplate.description",
              "Copiá este mensaje y enviálo al área de soporte de tu proveedor para pedir el certificado, la contraseña, las CFE y los CAE.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm">
            <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              {t(
                "onboarding.providerRequestTemplate.subjectLabel",
                "Asunto",
              )}
            </div>
            <div className="mt-1 text-neutral-900">{subject}</div>
          </div>

          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-4 text-sm leading-relaxed text-neutral-900 whitespace-pre-line">
            {/* data-mag-private: interpolates company name + RUT. */}
            <span data-mag-private>{body}</span>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common.close", "Cerrar")}
          </Button>
          <Button type="button" onClick={handleCopy}>
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                {t(
                  "onboarding.providerRequestTemplate.copiedButton",
                  "Copiado",
                )}
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                {t(
                  "onboarding.providerRequestTemplate.copyButton",
                  "Copiar plantilla",
                )}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
